/* Banking Portal — Money Map + Wave 1-4 pages (Feb 2026).

   Surfaces bank-sponsor-facing custody, reconciliation, compliance, and
   trust-and-security data. Backed by /api/partner/banking/* v2 routes.

   Pages exported below:
     - BankingMoneyMap        (custody + customers + movements + trace)
     - BankingReconciliation  (daily ledger recon + FBO attestation + stuck/NSF)
     - BankingCompliance      (KYB grid + sanctions log + AML feed + CTR)
     - BankingTrustSecurity   (security posture + control library + audit chain)
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Map, Banknote, ArrowDownToLine, ArrowUpFromLine, Activity, Search,
  RefreshCw, Scale, ScrollText, Lock, ShieldCheck, FileWarning, Clock,
  Building2, AlertTriangle, CheckCircle2, FileText, Network, Eye,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import {
  NachaMetricsPanel, MoneyMapConcentrationTab,
  TrustOperationsTab, TrustActivityTab, TrustVendorsTab,
} from "./BankingDiligencePages";
import {
  ScoreDistributionPanel,
} from "./BankingMirrorPages";

// ─── Shared helpers ──────────────────────────────────────────────────────
function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
}
function fmtCompact(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1, style: "currency", currency: "USD" }).format(n);
}
function fmtTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function MockedRibbon({ note }) {
  return (
    <div data-testid="mocked-ribbon" className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span><b className="font-semibold uppercase">Mocked:</b> {note}</span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// MONEY MAP — Wave 1 headline page
// ═══════════════════════════════════════════════════════════════════════
export function BankingMoneyMap() {
  const [tab, setTab] = useState("overview");

  return (
    <div data-testid="banking-money-map" className="space-y-5">
      <header>
        <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
          <Map className="w-6 h-6 text-sky-500" /> Money Map
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Live custody balance, wallet inventory, money movements, and the dollar-trace tool — everything a sponsor bank needs to answer <em>where is the money?</em>
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="mm-tabs">
          <TabsTrigger value="overview" data-testid="mm-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="wallets" data-testid="mm-tab-wallets">Wallets</TabsTrigger>
          <TabsTrigger value="customers" data-testid="mm-tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="movements" data-testid="mm-tab-movements">Movements</TabsTrigger>
          <TabsTrigger value="concentration" data-testid="mm-tab-concentration">Concentration</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <MoneyMapOverviewPanel />
        </TabsContent>
        <TabsContent value="wallets" className="space-y-4 mt-4">
          <MoneyMapWalletsPanel />
        </TabsContent>
        <TabsContent value="customers" className="space-y-4 mt-4">
          <MoneyMapCustomersPanel />
        </TabsContent>
        <TabsContent value="movements" className="space-y-4 mt-4">
          <MoneyMapMovementsPanel />
        </TabsContent>
        <TabsContent value="concentration" className="space-y-4 mt-4">
          <MoneyMapConcentrationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MoneyMapOverviewPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/banking/money-map/overview");
      setData(data);
    } catch (e) {
      toast.error("Could not load Money Map overview");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-slate-500" data-testid="mm-overview-loading">Loading…</div>;

  const segPct = data.fbo_segregation_pct;
  const segTone = segPct >= 99 ? "emerald" : segPct >= 90 ? "amber" : "rose";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mm-kpis">
        <Kpi testid="mm-kpi-custody"  label="Total custody"      value={fmtCompact(data.custody_total_usd)}  sub={`${fmtMoney(data.custody_total_usd)}`} icon={Banknote} tone="sky" />
        <Kpi testid="mm-kpi-customer" label="Customer funds"     value={fmtCompact(data.customer_funds_usd)} sub="held in DDA segregation" icon={Building2} tone="emerald" />
        <Kpi testid="mm-kpi-inflow"   label="Inflows 24h"        value={fmtCompact(data.inflow_24h.amount)}  sub={`${data.inflow_24h.count} txns`} icon={ArrowDownToLine} tone="emerald" />
        <Kpi testid="mm-kpi-outflow"  label="Outflows 24h"       value={fmtCompact(data.outflow_24h.amount)} sub={`${data.outflow_24h.count} txns`} icon={ArrowUpFromLine} tone="rose" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border bg-white p-4" data-testid="mm-fbo-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-500" /> DDA segregation
            </div>
            <Badge variant="outline" className={`bg-${segTone}-50 text-${segTone}-700 border-${segTone}-200`}>{segPct}%</Badge>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Customer funds (in DDAs)</span>
              <span className="tabular-nums font-medium">{fmtMoney(data.customer_funds_usd)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Operating funds (Verqo)</span>
              <span className="tabular-nums font-medium">{fmtMoney(data.operating_funds_usd)}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full bg-${segTone}-500`} style={{ width: `${Math.min(segPct, 100)}%` }} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4" data-testid="mm-net-card">
          <div className="text-sm font-medium text-slate-700 mb-3">Net flow · last 24h</div>
          <div className={`text-3xl tabular-nums font-semibold ${data.net_24h >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {data.net_24h >= 0 ? "+" : "−"}{fmtMoney(Math.abs(data.net_24h))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            inflow {fmtMoney(data.inflow_24h.amount)} − outflow {fmtMoney(data.outflow_24h.amount)}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-4" data-testid="mm-by-role">
        <div className="text-sm font-medium text-slate-700 mb-3">Wallets by role</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {Object.keys(data.wallets_by_role || {}).length === 0 && (
            <div className="text-sm text-slate-500 col-span-full">No wallets yet.</div>
          )}
          {Object.entries(data.wallets_by_role || {}).map(([role, v]) => (
            <div key={role} className="rounded-lg border bg-slate-50 px-3 py-2.5" data-testid={`mm-role-${role}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{role}</div>
              <div className="text-base font-semibold tabular-nums mt-0.5">{v.wallet_count.toLocaleString()}</div>
              <div className="text-[11px] text-slate-500">{fmtMoney(v.balance_total)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-slate-500" data-testid="mm-as-of">As of {fmtTime(data.as_of)}</div>
    </div>
  );
}

function MoneyMapWalletsPanel() {
  const [rows, setRows] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [minBal, setMinBal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200, min_balance: minBal };
      if (role !== "all") params.role = role;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/partner/banking/money-map/wallets", { params });
      setRows(data.rows);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, minBal]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40 h-9" data-testid="mm-wallets-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="buyer">Buyer</SelectItem>
            <SelectItem value="seller">Seller</SelectItem>
            <SelectItem value="lender_partner">Lender</SelectItem>
            <SelectItem value="insurance_partner">Insurance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(minBal)} onValueChange={v => setMinBal(Number(v))}>
          <SelectTrigger className="w-44 h-9" data-testid="mm-wallets-min"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any balance</SelectItem>
            <SelectItem value="100">$100+</SelectItem>
            <SelectItem value="1000">$1K+</SelectItem>
            <SelectItem value="10000">$10K+</SelectItem>
            <SelectItem value="100000">$100K+</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-slate-400" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="email · company · name"
            className="pl-7 h-9 w-64"
            data-testid="mm-wallets-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="mm-wallets-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500" data-testid="mm-wallets-count">{rows.length} wallets</div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Owner</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">KYB</div>
          <div className="col-span-2">EIN</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div data-testid="mm-wallets-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="mm-wallets-empty">No wallets match the filter.</div>
          )}
          {rows.map(r => (
            <div key={r.wallet_user_id} data-testid={`mm-wallet-row-${r.wallet_user_id}`} className="grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center">
              <div className="col-span-3 truncate">
                <div className="text-slate-900 truncate">{r.owner_company || r.owner_name || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.owner_email}</div>
              </div>
              <div className="col-span-2"><Badge variant="outline" className="text-[10px] uppercase">{r.owner_role || "—"}</Badge></div>
              <div className="col-span-2">{r.kyb_status ? <Badge variant="outline" className="text-[10px]">{r.kyb_status}</Badge> : <span className="text-xs text-slate-400">—</span>}</div>
              <div className="col-span-2 font-mono text-xs text-slate-600">{r.owner_ein || "—"}</div>
              <div className="col-span-2 tabular-nums text-right">{fmtMoney(r.balance)}</div>
              <div className="col-span-1 text-right">
                {r.is_frozen ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Frozen</Badge> : <span className="text-emerald-600 text-xs">●</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MoneyMapCustomersPanel() {
  const [rows, setRows] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = { limit: 200 };
    if (role !== "all") params.role = role;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/partner/banking/money-map/customers", { params });
    setRows(data.rows);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40 h-9" data-testid="mm-cust-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="buyer">Buyers</SelectItem>
            <SelectItem value="seller">Sellers</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-slate-400" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="email · company"
            className="pl-7 h-9 w-64"
            data-testid="mm-cust-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="mm-cust-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500" data-testid="mm-cust-count">{rows.length} customers</div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">KYB</div>
          <div className="col-span-2">Sanctions</div>
          <div className="col-span-1 text-right">Balance</div>
          <div className="col-span-2 text-right">Joined</div>
        </div>
        <div data-testid="mm-cust-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="mm-cust-empty">No customers found.</div>
          )}
          {rows.map(r => (
            <div key={r.id} data-testid={`mm-cust-row-${r.id}`} className="grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center">
              <div className="col-span-3 truncate">
                <div className="text-slate-900 truncate">{r.company || r.name || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.email}</div>
              </div>
              <div className="col-span-2"><Badge variant="outline" className="text-[10px] uppercase">{r.role || "—"}</Badge></div>
              <div className="col-span-2">{r.kyb_status ? <Badge variant="outline" className="text-[10px]">{r.kyb_status}</Badge> : <span className="text-xs text-slate-400">—</span>}</div>
              <div className="col-span-2 text-xs text-slate-500" title="Mocked until sanctions provider wired">{r.sanctions_status}</div>
              <div className="col-span-1 tabular-nums text-right text-xs">{fmtMoney(r.balance)}</div>
              <div className="col-span-2 text-right text-[11px] text-slate-500">{fmtTime(r.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MoneyMapMovementsPanel() {
  const [rows, setRows] = useState([]);
  const [direction, setDirection] = useState("all");
  const [role, setRole] = useState("all");
  const [days, setDays] = useState(7);
  const [openTxn, setOpenTxn] = useState(null);

  const load = async () => {
    const params = { since_days: days, limit: 300 };
    if (direction !== "all") params.direction = direction;
    if (role !== "all") params.role = role;
    const { data } = await api.get("/partner/banking/money-map/movements", { params });
    setRows(data.rows);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [direction, role, days]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="w-32 h-9" data-testid="mm-mov-direction"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All flow</SelectItem>
            <SelectItem value="in">Inflow</SelectItem>
            <SelectItem value="out">Outflow</SelectItem>
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40 h-9" data-testid="mm-mov-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="buyer">Buyers</SelectItem>
            <SelectItem value="seller">Sellers</SelectItem>
            <SelectItem value="lender_partner">Lenders</SelectItem>
            <SelectItem value="insurance_partner">Insurers</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="mm-mov-since"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="mm-mov-count">{rows.length} movements</div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Owner</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Trace</div>
        </div>
        <div data-testid="mm-mov-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="mm-mov-empty">No movements in window.</div>
          )}
          {rows.map(r => (
            <div key={r.id} data-testid={`mm-mov-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              <div className="col-span-2 font-mono text-xs">{r.type || "—"}</div>
              <div className="col-span-3 truncate">
                <div className="text-slate-700 truncate text-xs">{r.owner_company || r.owner_email || "—"}</div>
                <div className="text-[10px] text-slate-400">{r.owner_role || "—"}</div>
              </div>
              <div className={`col-span-2 tabular-nums text-right ${r.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {r.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(r.amount))}
              </div>
              <div className="col-span-2"><Badge variant="outline" className="text-[10px]">{r.status || "—"}</Badge></div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="ghost" onClick={() => setOpenTxn(r)} data-testid={`mm-mov-trace-${r.id}`}>
                  <Network className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TraceDollarDialog txn={openTxn} onClose={() => setOpenTxn(null)} />
    </div>
  );
}

function TraceDollarDialog({ txn, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!txn) { setDetail(null); return; }
    api.get(`/partner/banking/money-map/trace/${txn.id}`).then(r => setDetail(r.data)).catch(() => {});
  }, [txn?.id]);
  if (!txn) return null;

  return (
    <Dialog open={!!txn} onOpenChange={onClose}>
      <DialogContent data-testid="mm-trace-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Network className="w-4 h-4" /> Trace the dollar · {txn.id.slice(0, 8)}…</DialogTitle>
          <DialogDescription>Upstream owner · linked invoice · sibling fee splits · webhook events.</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500">Owner</div>
                {detail.owner.company || detail.owner.email || "—"}
                <div className="text-[10px] text-slate-400">{detail.owner.role}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500">Amount</div>
                <span className={`tabular-nums font-semibold ${detail.txn.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {detail.txn.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(detail.txn.amount))}
                </span>
              </div>
              {detail.invoice && (
                <div className="rounded-lg border bg-slate-50 px-3 py-2 col-span-2">
                  <div className="text-[10px] uppercase text-slate-500">Linked invoice</div>
                  <span className="font-mono text-xs">{detail.invoice.code || detail.invoice.id}</span>
                  <span className="text-xs text-slate-500 ml-2">{detail.invoice.status} · {fmtMoney(detail.invoice.amount)}</span>
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Sibling money movements ({detail.siblings.length})</div>
              <div className="rounded-lg border divide-y" data-testid="mm-trace-siblings">
                {detail.siblings.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">No siblings tied to the same invoice.</div>}
                {detail.siblings.map(s => (
                  <div key={s.id} className="px-3 py-2 text-sm grid grid-cols-12 items-center">
                    <div className="col-span-3 text-[11px] text-slate-500">{fmtTime(s.created_at)}</div>
                    <div className="col-span-3 font-mono text-xs">{s.type || "—"}</div>
                    <div className="col-span-4 truncate text-xs text-slate-600">{s.owner_company || s.owner_email}</div>
                    <div className={`col-span-2 tabular-nums text-right text-xs ${s.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {s.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(s.amount))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Linked webhook events ({detail.webhook_events.length})</div>
              <div className="rounded-lg border divide-y" data-testid="mm-trace-webhooks">
                {detail.webhook_events.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">No rails webhooks linked.</div>}
                {detail.webhook_events.map(e => (
                  <div key={e.id || e.event_id} className="px-3 py-2 text-xs flex items-center gap-2">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-500">{fmtTime(e.received_at)}</span>
                    <span className="font-mono">{e.event_type}</span>
                    {e.signature_ok === false && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">bad sig</Badge>}
                    {e.applied === false && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">unapplied</Badge>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// RECONCILIATION & SETTLEMENT (Wave 3)
// ═══════════════════════════════════════════════════════════════════════
export function BankingReconciliation() {
  const [daily, setDaily] = useState(null);
  const [fbo, setFbo] = useState(null);
  const [stuck, setStuck] = useState([]);
  const [returns, setReturns] = useState([]);
  const [days, setDays] = useState(14);

  const load = async () => {
    try {
      const [d, f, s, r] = await Promise.all([
        api.get("/partner/banking/recon/daily", { params: { days } }),
        api.get("/partner/banking/recon/fbo-attestation"),
        api.get("/partner/banking/recon/stuck-payments"),
        api.get("/partner/banking/recon/returns-nsf", { params: { days: 30 } }),
      ]);
      setDaily(d.data);
      setFbo(f.data);
      setStuck(s.data.rows || []);
      setReturns(r.data.rows || []);
    } catch (e) {
      toast.error("Could not load reconciliation");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="banking-recon" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Scale className="w-6 h-6 text-sky-500" /> Reconciliation & Settlement
          </h1>
          <p className="text-sm text-slate-600 mt-1">Daily ledger recon, live DDA attestation, stuck payments and ACH returns.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="recon-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="recon-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </header>

      {fbo && (
        <div className="rounded-2xl border bg-white p-4" data-testid="recon-fbo-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-500" /> DDA attestation · live
            </div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{fbo.segregation_pct}% segregated</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Kpi testid="recon-fbo-total" label="Total custody" value={fmtCompact(fbo.total_custody_usd)} sub={fmtMoney(fbo.total_custody_usd)} tone="sky" />
            <Kpi testid="recon-fbo-customer" label="Customer funds" value={fmtCompact(fbo.customer_funds_usd)} tone="emerald" />
            <Kpi testid="recon-fbo-operating" label="Operating funds" value={fmtCompact(fbo.operating_funds_usd)} tone="slate" />
          </div>
          <p className="text-xs text-slate-500 mt-3 italic">{fbo.attestation_text}</p>
        </div>
      )}

      {daily && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-kpis">
          <Kpi testid="recon-kpi-days"     label={`Recon days · ${days}d`} value={daily.days.length} icon={Activity} tone="slate" />
          <Kpi testid="recon-kpi-stuck"    label="Stuck >48h"              value={daily.stuck.toLocaleString()} icon={Clock} tone={daily.stuck ? "amber" : "emerald"} />
          <Kpi testid="recon-kpi-returns"  label="Returns 30d"             value={daily.returns_30d.toLocaleString()} icon={AlertTriangle} tone={daily.returns_30d ? "rose" : "emerald"} />
          <Kpi testid="recon-kpi-asof"     label="As of"                   value={fmtTime(daily.as_of).split(",")[0]} tone="slate" sub={fmtTime(daily.as_of).split(",")[1]} />
        </div>
      )}

      {daily && (
        <div className="rounded-2xl border bg-white overflow-hidden" data-testid="recon-daily-table">
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Daily settlement ledger</div>
          <div className="grid grid-cols-12 px-4 py-2 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <div className="col-span-3">Day</div>
            <div className="col-span-2 text-right">Inflow</div>
            <div className="col-span-2 text-right">Outflow</div>
            <div className="col-span-2 text-right">Net</div>
            <div className="col-span-3 text-right">Txns</div>
          </div>
          {daily.days.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="recon-daily-empty">No settled transactions in window.</div>
          )}
          {daily.days.map(d => (
            <div key={d.day} data-testid={`recon-day-${d.day}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-3 font-mono text-xs">{d.day}</div>
              <div className="col-span-2 tabular-nums text-right text-emerald-700">+{fmtMoney(d.inflow)}</div>
              <div className="col-span-2 tabular-nums text-right text-rose-700">−{fmtMoney(d.outflow)}</div>
              <div className={`col-span-2 tabular-nums text-right ${d.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{d.net >= 0 ? "+" : "−"}{fmtMoney(Math.abs(d.net))}</div>
              <div className="col-span-3 text-right text-xs text-slate-500">{d.txns.toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border bg-white overflow-hidden" data-testid="recon-stuck-list">
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-500" /> Stuck payments &gt; 48h</span>
            <Badge variant="outline" className="text-[10px]">{stuck.length}</Badge>
          </div>
          {stuck.length === 0 && <div className="text-center text-sm text-slate-500 py-8" data-testid="recon-stuck-empty"><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No stuck payments.</div>}
          {stuck.slice(0, 10).map(r => (
            <div key={r.id} data-testid={`recon-stuck-row-${r.id}`} className="px-4 py-2 border-b border-slate-100 text-sm flex items-center justify-between">
              <div>
                <div className="font-mono text-xs">{r.id.slice(0, 8)}…</div>
                <div className="text-[11px] text-slate-500">{fmtTime(r.created_at)} · {r.method || r.type}</div>
              </div>
              <div className="tabular-nums">{fmtMoney(r.amount)}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border bg-white overflow-hidden" data-testid="recon-returns-list">
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b flex items-center justify-between">
            <span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-rose-500" /> ACH returns / NSF (30d)</span>
            <Badge variant="outline" className="text-[10px]">{returns.length}</Badge>
          </div>
          {returns.length === 0 && <div className="text-center text-sm text-slate-500 py-8" data-testid="recon-returns-empty"><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No returns in window.</div>}
          {returns.slice(0, 10).map(r => (
            <div key={r.id} data-testid={`recon-return-row-${r.id}`} className="px-4 py-2 border-b border-slate-100 text-sm flex items-center justify-between">
              <div>
                <div className="font-mono text-xs">{r.id.slice(0, 8)}…</div>
                <div className="text-[11px] text-slate-500">{fmtTime(r.created_at)} · {r.failure_reason || r.status}</div>
              </div>
              <div className="tabular-nums">{fmtMoney(r.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// COMPLIANCE & RISK (Wave 4)
// ═══════════════════════════════════════════════════════════════════════
export function BankingCompliance() {
  const [tab, setTab] = useState("kyb");

  return (
    <div data-testid="banking-compliance" className="space-y-5">
      <header>
        <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
          <ScrollText className="w-6 h-6 text-sky-500" /> Compliance & Risk
        </h1>
        <p className="text-sm text-slate-600 mt-1">KYB attestation grid, sanctions screening, AML alerts, and CTR-threshold transaction feed.</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="compl-tabs">
          <TabsTrigger value="kyb" data-testid="compl-tab-kyb">KYB grid</TabsTrigger>
          <TabsTrigger value="sanctions" data-testid="compl-tab-sanctions">Sanctions</TabsTrigger>
          <TabsTrigger value="aml" data-testid="compl-tab-aml">AML alerts</TabsTrigger>
          <TabsTrigger value="ctr" data-testid="compl-tab-ctr">CTR threshold</TabsTrigger>
          <TabsTrigger value="nacha" data-testid="compl-tab-nacha">NACHA</TabsTrigger>
          <TabsTrigger value="score-dist" data-testid="compl-tab-score-dist">Score distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="kyb" className="mt-4 space-y-3">
          <ComplianceKybPanel />
        </TabsContent>
        <TabsContent value="sanctions" className="mt-4 space-y-3">
          <ComplianceSanctionsPanel />
        </TabsContent>
        <TabsContent value="aml" className="mt-4 space-y-3">
          <ComplianceAmlPanel />
        </TabsContent>
        <TabsContent value="ctr" className="mt-4 space-y-3">
          <ComplianceCtrPanel />
        </TabsContent>
        <TabsContent value="nacha" className="mt-4 space-y-3">
          <NachaMetricsPanel />
        </TabsContent>
        <TabsContent value="score-dist" className="mt-4 space-y-3">
          <ScoreDistributionPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ComplianceKybPanel() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");

  const load = async () => {
    const params = { limit: 200 };
    if (status !== "all") params.status = status;
    const { data } = await api.get("/partner/banking/compliance/kyb-grid", { params });
    setRows(data.rows);
    setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kyb-strip">
        {["clear", "review", "fail", "pending"].map(s => (
          <Kpi key={s} testid={`kyb-strip-${s}`} label={s} value={(byStatus[s] || 0).toLocaleString()} tone={s === "clear" ? "emerald" : s === "fail" ? "rose" : s === "review" ? "amber" : "slate"} />
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32 h-9" data-testid="kyb-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="clear">Clear</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="fail">Fail</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="kyb-count">{rows.length} rows</div>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-4">Customer</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">EIN</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2 text-right">Verified</div>
        </div>
        <div data-testid="kyb-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="kyb-empty">No KYB records.</div>
          )}
          {rows.map(r => {
            const tone = r.status === "clear" ? "emerald" : r.status === "fail" ? "rose" : r.status === "review" ? "amber" : "slate";
            return (
              <div key={r.user_id} data-testid={`kyb-row-${r.user_id}`} className="grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center">
                <div className="col-span-4 truncate">
                  <div className="text-slate-900 truncate">{r.owner_company || r.owner_email}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.owner_email}</div>
                </div>
                <div className="col-span-2"><Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{r.status}</Badge></div>
                <div className="col-span-2 font-mono text-xs text-slate-600">{r.owner_ein || "—"}</div>
                <div className="col-span-2 text-xs text-slate-500">{r.owner_role || "—"}</div>
                <div className="col-span-2 text-right text-[11px] text-slate-500">{fmtTime(r.created_at)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ComplianceSanctionsPanel() {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  useEffect(() => {
    api.get("/partner/banking/compliance/sanctions-log").then(r => setData(r.data));
    api.get("/partner/banking/compliance/sanctions-log/review-queue?limit=100").then(r => setQueue(r.data.rows || []));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="sanctions-loading">Loading…</div>;

  const liveBadge = data.provider && data.provider.startsWith("ofac_sdn");

  return (
    <>
      {liveBadge && (
        <div data-testid="sanctions-live-ribbon" className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b className="font-semibold uppercase">Live · OFAC SDN</b> — screening against the US Treasury's Specially Designated Nationals list ({(data.sdn_list_size || 0).toLocaleString()} entries · loaded {fmtTime(data.sdn_list_loaded_at)}). Daily re-screen of every customer.</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sanctions-kpis">
        <Kpi testid="sanctions-total"  label="Total screens"  value={(data.screens_total  || 0).toLocaleString()} icon={Eye} tone="slate" />
        <Kpi testid="sanctions-clear"  label="Clear"          value={(data.screens_clear  || 0).toLocaleString()} icon={CheckCircle2} tone="emerald" />
        <Kpi testid="sanctions-match"  label="Matches"        value={(data.screens_match  || 0).toLocaleString()} icon={AlertTriangle} tone={data.screens_match ? "rose" : "slate"} />
        <Kpi testid="sanctions-review" label="Manual review"  value={(data.screens_review || 0).toLocaleString()} icon={FileWarning} tone={data.screens_review ? "amber" : "slate"} />
      </div>
      <div className="rounded-2xl border bg-white p-4 text-sm text-slate-700" data-testid="sanctions-meta">
        <div className="flex items-center justify-between">
          <span>Provider: <span className="font-mono text-xs">{data.provider}</span></span>
          <span>Last scan: <span className="font-mono text-xs">{fmtTime(data.last_screen_at)}</span></span>
        </div>
        <div className="text-xs text-slate-500 mt-1">Next rescan window: {fmtTime(data.next_rescan_at)}</div>
      </div>

      {queue.length > 0 && (
        <div className="rounded-2xl border bg-white overflow-hidden" data-testid="sanctions-review-queue">
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b flex items-center gap-2">
            <FileWarning className="w-4 h-4 text-amber-500" /> Review queue
            <Badge variant="outline" className="text-[10px]">{queue.length}</Badge>
          </div>
          {queue.map(r => (
            <div key={r.user_id} data-testid={`sanctions-q-${r.user_id}`} className="px-4 py-2.5 border-b border-slate-100 text-sm flex items-center justify-between">
              <div className="truncate">
                <div className="text-slate-900 truncate">{r.target}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.owner_email}</div>
              </div>
              <Badge variant="outline" className={`text-[10px] ${r.status === "match" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {r.status} · top score {r.matches?.[0]?.score ?? "—"}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ComplianceAmlPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [severity, setSeverity] = useState("all");
  const [kind, setKind] = useState("all");

  const load = async () => {
    const params = { days };
    if (severity !== "all") params.severity = severity;
    if (kind !== "all") params.kind = kind;
    const { data } = await api.get("/partner/banking/compliance/aml-alerts", { params });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, severity, kind]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="aml-loading">Loading…</div>;

  const rules = data.summary?.rules_active || [];

  return (
    <>
      <div data-testid="aml-live-ribbon" className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span><b className="font-semibold uppercase">Live · AML engine v1</b> — {rules.length} deterministic rules scanned every 30 min: {rules.join(" · ")}.</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="aml-kpis">
        <Kpi testid="aml-kpi-total"     label={`Alerts · ${days}d`} value={data.total.toLocaleString()} icon={AlertTriangle} tone={data.total ? "amber" : "emerald"} />
        <Kpi testid="aml-kpi-critical"  label="Critical"           value={(data.summary?.by_severity?.critical || 0).toLocaleString()} tone={data.summary?.by_severity?.critical ? "rose" : "slate"} />
        <Kpi testid="aml-kpi-high"      label="High"               value={(data.summary?.by_severity?.high || 0).toLocaleString()} tone={data.summary?.by_severity?.high ? "amber" : "slate"} />
        <Kpi testid="aml-kpi-open"      label="Open"               value={(data.summary?.by_resolution?.open || 0).toLocaleString()} tone="slate" />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="aml-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-36 h-9" data-testid="aml-filter-severity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-40 h-9" data-testid="aml-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rules</SelectItem>
            <SelectItem value="structuring">Structuring</SelectItem>
            <SelectItem value="rapid_fire">Rapid-fire</SelectItem>
            <SelectItem value="round_amount">Round-amount</SelectItem>
            <SelectItem value="circular_flow">Circular flow</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="aml-count">{data.total} alerts</div>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-2">Rule</div>
          <div className="col-span-2">Severity</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Details</div>
          <div className="col-span-1">Status</div>
        </div>
        <div data-testid="aml-table">
          {data.rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="aml-empty"><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No AML alerts in window.</div>
          )}
          {data.rows.map(r => {
            const tone = r.severity === "critical" ? "rose" : r.severity === "high" ? "amber" : "slate";
            return (
              <div key={r.id} data-testid={`aml-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
                <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.triggered_at)}</div>
                <div className="col-span-2 font-mono text-xs">{r.kind}</div>
                <div className="col-span-2"><Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{r.severity || "—"}</Badge></div>
                <div className="col-span-3 truncate text-xs">
                  <div className="truncate">{r.owner_company || r.owner_email || r.user_id || "—"}</div>
                  <div className="text-[10px] text-slate-400 truncate">{r.owner_role || "—"}</div>
                </div>
                <div className="col-span-2 truncate text-xs text-slate-600">
                  {r.details?.count && <span>{r.details.count}× </span>}
                  {r.details?.total && <span>· ${r.details.total.toLocaleString()}</span>}
                  {r.details?.window_seconds && <span>{r.details.window_seconds}s</span>}
                </div>
                <div className="col-span-1 text-xs text-slate-500">{r.resolution || "open"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ComplianceCtrPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [threshold, setThreshold] = useState(10000);

  const load = async () => {
    const { data } = await api.get("/partner/banking/compliance/ctr-threshold", { params: { days, threshold } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, threshold]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="ctr-loading">Loading…</div>;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="ctr-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(threshold)} onValueChange={v => setThreshold(Number(v))}>
          <SelectTrigger className="w-36 h-9" data-testid="ctr-threshold"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="5000">$5,000</SelectItem>
            <SelectItem value="10000">$10,000</SelectItem>
            <SelectItem value="25000">$25,000</SelectItem>
            <SelectItem value="50000">$50,000</SelectItem>
            <SelectItem value="100000">$100,000</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="ctr-count">{data.total} txns ≥ {fmtMoney(threshold)}</div>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Method</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1">Status</div>
        </div>
        <div data-testid="ctr-table">
          {data.rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="ctr-empty"><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No transactions at or above {fmtMoney(threshold)}.</div>
          )}
          {data.rows.map(r => (
            <div key={r.id} data-testid={`ctr-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              <div className="col-span-2 tabular-nums text-right">{fmtMoney(Math.abs(r.amount))}</div>
              <div className="col-span-3 truncate">{r.owner_company || r.owner_email}</div>
              <div className="col-span-2 text-xs text-slate-500">{r.method || "—"}</div>
              <div className="col-span-2 text-xs text-slate-500">{r.type || "—"}</div>
              <div className="col-span-1 text-xs text-slate-500">{r.status || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 px-1">
        Visibility only — Verqo's compliance team is responsible for actual CTR (FinCEN form 112) filings.
      </p>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// TRUST & SECURITY (Wave 2)
// ═══════════════════════════════════════════════════════════════════════
export function BankingTrustSecurity() {
  const [tab, setTab] = useState("posture");

  return (
    <div data-testid="banking-trust" className="space-y-5">
      <header>
        <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
          <Lock className="w-6 h-6 text-sky-500" /> Trust & Security
        </h1>
        <p className="text-sm text-slate-600 mt-1">Live security posture, control library, audit-chain integrity, incident register, and SOC document index.</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="trust-tabs">
          <TabsTrigger value="posture"    data-testid="trust-tab-posture">Security posture</TabsTrigger>
          <TabsTrigger value="operations" data-testid="trust-tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="controls"   data-testid="trust-tab-controls">Control library</TabsTrigger>
          <TabsTrigger value="audit"      data-testid="trust-tab-audit">Audit chain</TabsTrigger>
          <TabsTrigger value="activity"   data-testid="trust-tab-activity">Activity log</TabsTrigger>
          <TabsTrigger value="vendors"    data-testid="trust-tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="incidents"  data-testid="trust-tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="soc"        data-testid="trust-tab-soc">SOC library</TabsTrigger>
        </TabsList>

        <TabsContent value="posture"    className="mt-4 space-y-3"><TrustPosturePanel /></TabsContent>
        <TabsContent value="operations" className="mt-4 space-y-3"><TrustOperationsTab /></TabsContent>
        <TabsContent value="controls"   className="mt-4 space-y-3"><TrustControlsPanel /></TabsContent>
        <TabsContent value="audit"      className="mt-4 space-y-3"><TrustAuditPanel /></TabsContent>
        <TabsContent value="activity"   className="mt-4 space-y-3"><TrustActivityTab /></TabsContent>
        <TabsContent value="vendors"    className="mt-4 space-y-3"><TrustVendorsTab /></TabsContent>
        <TabsContent value="incidents"  className="mt-4 space-y-3"><TrustIncidentsPanel /></TabsContent>
        <TabsContent value="soc"        className="mt-4 space-y-3"><TrustSocPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function TrustPosturePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/security-posture").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="trust-posture-loading">Loading…</div>;

  const modeTone = data.operating_mode === "normal" ? "emerald" : data.operating_mode === "read-only" ? "amber" : "rose";

  return (
    <>
      <div className="rounded-2xl border bg-white p-4 flex items-center justify-between" data-testid="trust-mode-card">
        <div>
          <div className="text-sm font-medium text-slate-700">Platform operating mode</div>
          <div className="text-[11px] text-slate-500">Kill-switch status · 5 levels supported</div>
        </div>
        <Badge variant="outline" className={`bg-${modeTone}-50 text-${modeTone}-700 border-${modeTone}-200 uppercase text-xs px-3 py-1`} data-testid="trust-mode-badge">
          {data.operating_mode}
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trust-kpis">
        <Kpi testid="trust-threat-30d"   label="Threat events 30d"      value={(data.threat_events_30d).toLocaleString()} icon={AlertTriangle} tone={data.threat_events_30d ? "amber" : "emerald"} />
        <Kpi testid="trust-critical-30d" label="Critical 30d"           value={(data.critical_events_30d).toLocaleString()} icon={AlertTriangle} tone={data.critical_events_30d ? "rose" : "emerald"} />
        <Kpi testid="trust-blocked-ips"  label="Blocked IPs · current"  value={(data.blocked_ips_current).toLocaleString()} icon={ShieldCheck} tone="slate" />
        <Kpi testid="trust-lockouts"     label="Login lockouts 30d"     value={(data.login_lockouts_30d).toLocaleString()} icon={Lock} tone="slate" />
        <Kpi testid="trust-webhooks-30d" label="Webhooks 30d"           value={(data.webhooks_30d).toLocaleString()} icon={Activity} tone="slate" />
        <Kpi testid="trust-bad-sig-30d"  label="Bad signatures 30d"     value={(data.webhooks_bad_sig_30d).toLocaleString()} icon={AlertTriangle} tone={data.webhooks_bad_sig_30d ? "rose" : "emerald"} />
        <Kpi testid="trust-pii-reveals"  label="PII reveals 30d"        value={(data.pii_reveals_30d).toLocaleString()} icon={Eye} tone="slate" />
        <Kpi testid="trust-pii-wrong"    label="PII wrong pw 30d"       value={(data.pii_wrong_password_30d).toLocaleString()} icon={AlertTriangle} tone={data.pii_wrong_password_30d ? "rose" : "emerald"} />
      </div>
      <div className="text-[11px] text-slate-500">As of {fmtTime(data.as_of)}</div>
    </>
  );
}

function TrustControlsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/control-library").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="trust-controls-loading">Loading…</div>;

  const byCat = data.controls.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="trust-controls-list">
      {Object.entries(byCat).map(([cat, items]) => (
        <div key={cat} className="rounded-2xl border bg-white overflow-hidden" data-testid={`trust-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b flex items-center justify-between">
            <span>{cat}</span>
            <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
          </div>
          {items.map(c => (
            <div key={c.id} data-testid={`trust-ctrl-${c.id}`} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{c.summary}</div>
                </div>
                <div className="font-mono text-[10px] text-slate-400 shrink-0">{c.evidence_path}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function TrustAuditPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/audit-chain-status").then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <div className="text-sm text-slate-500" data-testid="trust-audit-loading">Loading…</div>;

  return (
    <>
      <div className="rounded-2xl border bg-white p-4" data-testid="trust-audit-card">
        <div className="flex items-center gap-2 mb-2">
          {data.verified ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Chain verified
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Chain integrity FAILED
            </Badge>
          )}
          {data._fallback && <Badge variant="outline" className="text-[10px]">fallback</Badge>}
        </div>
        <div className="text-sm text-slate-700">
          Hash-chained audit log row count: <span className="font-mono">{(data.rows_total || data.total_rows || 0).toLocaleString()}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Every privileged action — admin overrides, partner mutations, PII reveals, kill-switch flips — is appended to a SHA-256 hash chain that cannot be retroactively edited without breaking the chain. As of {fmtTime(data.as_of)}.
        </div>
      </div>
    </>
  );
}

function TrustIncidentsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/incident-register").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="trust-inc-loading">Loading…</div>;

  return (
    <>
      {data._mocked && <MockedRibbon note={data._mock_note} />}
      <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="trust-inc-card">
        <div className="text-sm font-medium text-slate-700">Open + closed incidents</div>
        {data.incidents.length === 0 ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> No incidents on record.</div>
        ) : (
          data.incidents.map(i => (
            <div key={i.id} className="border rounded-lg px-3 py-2 text-sm">{i.title}</div>
          ))
        )}
        <p className="text-xs text-slate-500 italic">{data.attestation_text}</p>
        <div className="text-[11px] text-slate-400">As of {fmtTime(data.as_of)}</div>
      </div>
    </>
  );
}

function TrustSocPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/partner/banking/trust/soc-library").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="trust-soc-loading">Loading…</div>;

  const API = process.env.REACT_APP_BACKEND_URL;

  return (
    <>
      {!data.any_real_docs && (
        <div data-testid="trust-soc-placeholder-ribbon" className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b className="font-semibold uppercase">Placeholder PDFs</b> — audited SOC / pen-test artefacts are in preparation. Each card below serves a watermarked placeholder; admins upload real PDFs via <span className="font-mono">/admin/compliance-docs</span>.</span>
        </div>
      )}

      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="trust-soc-docs">
        <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Compliance documents</div>
        {data.documents.map(d => {
          const url = `${API}/api/partner/banking/trust/soc-library/${d.id}/pdf`;
          return (
            <div key={d.id} data-testid={`trust-soc-doc-${d.id}`} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-slate-900 truncate">{d.title}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {d.nda_required && <span className="text-[10px] text-amber-600">NDA required</span>}
                    {d.target_q && d.is_placeholder && <span className="text-[10px] text-slate-500">target: {d.target_q}</span>}
                    {d.version && <span className="text-[10px] font-mono text-slate-500">v{d.version}</span>}
                    {d.uploaded_at && <span className="text-[10px] text-slate-500">uploaded {fmtTime(d.uploaded_at).split(",")[0]}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {d.is_placeholder ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid={`trust-soc-${d.id}-placeholder-badge`}>Placeholder</Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid={`trust-soc-${d.id}-available-badge`}>Available</Badge>
                )}
                <Button asChild size="sm" variant="outline" data-testid={`trust-soc-${d.id}-download`}>
                  <a href={url} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5 mr-1" /> {d.is_placeholder ? "Placeholder PDF" : "Download"}</a>
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="trust-soc-subproc">
        <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Sub-processors</div>
        {data.sub_processors.map(s => (
          <div key={s.name} className="px-4 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-900">{s.name}</div>
              <div className="text-[11px] text-slate-500">{s.purpose}</div>
            </div>
            {s.soc_available && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">SOC 2</Badge>}
          </div>
        ))}
      </div>
    </>
  );
}
